import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtDate = v => {
  if (!v) return '–';
  try { return format(parseISO(v), 'd MMM yyyy'); } catch { return '–'; }
};

const COLS = [
  { key: 'full_name',          label: 'Full Name' },
  { key: 'dob',                label: 'DOB',              render: v => fmtDate(v) },
  { key: 'gender',             label: 'Gender',           render: v => v || '–' },
  { key: 'ndis_number',        label: 'NDIS Number',      render: v => v || '–' },
  { key: 'client_type',        label: 'Client Type',      render: v => v ? v.replace(/_/g, ' ') : '–' },
  { key: 'address',            label: 'Address',          render: v => v || '–' },
  { key: 'suburb',             label: 'Suburb',           render: v => v || '–' },
  { key: 'state',              label: 'State',            render: v => v || '–' },
  { key: 'primary_disability', label: 'Primary Disability', render: v => v || '–' },
  { key: 'plan_period_start',  label: 'Plan Start',       render: v => fmtDate(v) },
  { key: 'plan_period_end',    label: 'Plan End',         render: v => fmtDate(v) },
  { key: 'next_review_date',   label: 'Next Review',      render: v => fmtDate(v) },
  { key: 'joined_date',        label: 'Joined Date',      render: v => fmtDate(v) },
];

export default function ClientListReport() {
  const [allData, setAllData]   = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = allData.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.full_name || '').toLowerCase().includes(q) ||
           (r.ndis_number || '').toLowerCase().includes(q);
  });

  const pagination = usePagination(filtered);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/client-list')
      .then(r => { setAllData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const chips = [
    ...(search ? [{ label: 'Search', value: search, onRemove: () => setSearch('') }] : []),
  ];

  return (
    <ReportLayout
      title="Active Client List"
      data={filtered}
      filename="client_list_active"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Search Name / NDIS Number</label>
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); pagination.reset(); }}
              placeholder="Search…"
              className="input-field !w-auto text-sm"
            />
          </div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Refresh</button>
          </div>
        </div>
      }
    >
      <div className="flex items-center gap-4 text-sm text-slate-600 font-medium">
        <span>Total active clients: <strong className="text-slate-900">{filtered.length}</strong></span>
      </div>
      <ReportTable
        columns={COLS}
        data={filtered}
        loading={loading}
        pagination={pagination}
        emptyMsg="No active clients found."
      />
    </ReportLayout>
  );
}
